import type { NormalizeIssue, SchemaVersion } from './types';

export interface DetectedSchema {
  readonly schemaVersion: SchemaVersion;
  readonly issues: readonly NormalizeIssue[];
}

type ContainerKey = 'boxes' | 'drawableObjects';

function collageContainerKey(collage: unknown): ContainerKey | undefined {
  if (!collage || typeof collage !== 'object') return undefined;
  const record = collage as Record<string, unknown>;
  if (Array.isArray(record['boxes'])) return 'boxes';
  if (Array.isArray(record['drawableObjects'])) return 'drawableObjects';
  return undefined;
}

function containerKeyToSchema(key: ContainerKey): SchemaVersion {
  return key === 'boxes' ? 'custom-template-v1' : 'legacy-drawable-v0';
}

function collectCollages(doc: Record<string, unknown>): readonly unknown[] {
  const collages: unknown[] = [];
  const pushCollagesOf = (spread: unknown): void => {
    if (!spread || typeof spread !== 'object') return;
    const arr = (spread as Record<string, unknown>)['collages'];
    if (Array.isArray(arr)) collages.push(...arr);
  };

  pushCollagesOf(doc['cover']);
  const spreads = doc['spreads'];
  if (Array.isArray(spreads)) {
    for (const spread of spreads) pushCollagesOf(spread);
  }

  return collages;
}

/**
 * Detects across ALL collages, not just the first non-empty one — a document hand-patched by a
 * hotfix can mix `boxes` and `drawableObjects` collages; trusting only the first risks silently
 * mis-decoding the rest.
 */
export function detectSchemaVersion(raw: unknown): DetectedSchema {
  if (raw === null || raw === undefined || typeof raw !== 'object') {
    return { schemaVersion: 'legacy-drawable-v0', issues: [] };
  }

  const doc = raw as Record<string, unknown>;
  const collages = collectCollages(doc);
  const keys = collages.map(collageContainerKey).filter((key): key is ContainerKey => key !== undefined);

  const issues: NormalizeIssue[] = [];
  let structural: SchemaVersion | undefined;
  if (keys.length > 0) {
    structural = containerKeyToSchema(keys[0]);
    const isMixed = keys.some(key => containerKeyToSchema(key) !== structural);
    if (isMixed) {
      issues.push({
        severity: 'warn',
        path: 'collages',
        code: 'mixed-schema-shape',
        reason: 'mixed schema shape across collages (some `boxes`, some `drawableObjects`) — trusting the first collage',
      });
    }
  }

  const declared = doc['schemaVersion'];
  if (declared === 'custom-template-v1') {
    if (structural === undefined || structural === 'custom-template-v1') {
      return { schemaVersion: 'custom-template-v1', issues };
    }

    issues.push({
      severity: 'warn',
      path: 'schemaVersion',
      reason: 'declared custom-template-v1 but collage shape is legacy drawableObjects — trusting structure over the field',
    });
    return { schemaVersion: 'legacy-drawable-v0', issues };
  }

  if (structural !== undefined) {
    return { schemaVersion: structural, issues };
  }

  return { schemaVersion: 'legacy-drawable-v0', issues };
}

/**
 * Судит ли детекция по первому коллажу, потому что формы внутри книги смешаны. Читатели этой границы
 * живут в разных приложениях, и раньше каждый сравнивал текст `reason` со своей копией строки —
 * переформулированное сообщение молча открывало бы им обоим не тот путь.
 *
 * Граница у самой детекции уже: смешанной считается только книга, где у коллажей есть контейнер
 * (`collageContainerKey`). Коллаж без `boxes` и без `drawableObjects` в счёт не идёт — его так же
 * сознательно пропускают мутаторы персист-формы (`applyFullBleedBackgroundToCollageBoxes`).
 */
export function isMixedShape(detected: DetectedSchema): boolean {
  return detected.issues.some(issue => issue.code === 'mixed-schema-shape');
}
